import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ReceiptReviewPanel.css';
import { formatDateRD, formatDateShortRD, formatDateTimeRD, toDateTimeLocalRD } from '../utils/dateUtils';

const ReceiptReviewPanel = ({ isOpen, onClose }) => {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [reviewData, setReviewData] = useState({
    amount: '',
    bank: 'BANRESERVAS',
    type: 'transferencia',
    date: '',
    time: '',
    notes: ''
  });
  const [applyingPayment, setApplyingPayment] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [imageZoomed, setImageZoomed] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [clientDebt, setClientDebt] = useState(null);
  const [loadingDebt, setLoadingDebt] = useState(false);
  const [manualMikrowispId, setManualMikrowispId] = useState('');
  const [savingMikrowispId, setSavingMikrowispId] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadReceipts();
    }
  }, [isOpen]);

  const loadReceipts = async () => {
    try {
      setLoading(true);
      setMessage({ type: '', text: '' }); // Limpiar mensajes anteriores
      
      console.log('[INFO] Cargando recibos pendientes...');
      const response = await axios.get('/api/receipts/pending');
      
      console.log('[INFO] Respuesta recibida:', response.data);
      
      if (response.data && response.data.success) {
        // Ordenar: primero los pendientes, luego los aplicados automticamente
        const sortedReceipts = (response.data.receipts || []).sort((a, b) => {
          // Si uno es auto-applied y el otro no, el no auto-applied va primero
          if (a.autoApplied && !b.autoApplied) return 1;
          if (!a.autoApplied && b.autoApplied) return -1;
          // Si ambos tienen el mismo estado, ordenar por fecha (ms recientes primero)
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
        setReceipts(sortedReceipts);
        console.log(` ${sortedReceipts.length} recibos cargados`);
      } else {
        console.error('[ERROR] Respuesta sin success:', response.data);
        setMessage({ type: 'error', text: response.data?.error || 'Error al cargar recibos pendientes' });
      }
    } catch (error) {
      console.error('[ERROR] Error cargando recibos:', error);
      console.error('   Response:', error.response?.data);
      console.error('   Status:', error.response?.status);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || error.message || 'Error al cargar recibos pendientes' 
      });
    } finally {
      setLoading(false);
    }
  };

  const loadClientDebt = async (mikrowispId) => {
    try {
      setLoadingDebt(true);
      setClientDebt(null);
      
      console.log('[INFO] Cargando deuda del cliente:', mikrowispId);
      const response = await axios.post('/api/contacts/get-client-info', {
        mikrowispId: parseInt(mikrowispId)
      });
      
      if (response.data && response.data.success) {
        const clientData = response.data.client;
        const debtAmount = parseFloat(clientData.facturacion?.total_facturas || 0);
        const unpaidInvoices = parseInt(clientData.facturacion?.facturas_nopagadas || 0);
        
        setClientDebt({
          amount: debtAmount,
          unpaidInvoices: unpaidInvoices,
          clientName: clientData.nombre || 'Cliente'
        });
        
        console.log('[OK] Deuda cargada:', debtAmount);
      } else {
        console.error('[ERROR] Error en respuesta de deuda:', response.data);
      }
    } catch (error) {
      console.error('[ERROR] Error cargando deuda del cliente:', error);
    } finally {
      setLoadingDebt(false);
    }
  };

  const handleSaveMikrowispId = async () => {
    if (!manualMikrowispId || !selectedReceipt) return;

    try {
      setSavingMikrowispId(true);
      setMessage({ type: '', text: '' });

      console.log(' Guardando ID de Mikrowisp:', manualMikrowispId);
      
      // Actualizar el contacto con el ID de Mikrowisp
      const response = await axios.post(`/api/contacts/${selectedReceipt.phoneNumber}/update-mikrowisp-id`, {
        mikrowispId: parseInt(manualMikrowispId)
      });

      if (response.data && response.data.success) {
        setMessage({ type: 'success', text: 'ID de Mikrowisp guardado correctamente' });
        
        // Actualizar el recibo seleccionado con el nuevo ID
        const updatedReceipt = {
          ...selectedReceipt,
          contact: {
            ...selectedReceipt.contact,
            mikrowispId: parseInt(manualMikrowispId)
          }
        };
        setSelectedReceipt(updatedReceipt);
        
        // Cargar la deuda del cliente
        loadClientDebt(manualMikrowispId);
        
        // Recargar la lista de recibos
        setTimeout(() => {
          loadReceipts();
        }, 1000);
      } else {
        setMessage({ type: 'error', text: response.data?.error || 'Error al guardar ID de Mikrowisp' });
      }
    } catch (error) {
      console.error('âŒ Error guardando ID de Mikrowisp:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Error al guardar ID de Mikrowisp' 
      });
    } finally {
      setSavingMikrowispId(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedReceipt) return;

    // Advertencia para recibos duplicados
    if (selectedReceipt.status === 'duplicate') {
      const duplicateId = selectedReceipt.verificationResult?.duplicateReceiptId || 'desconocido';
      const clientInfo = selectedReceipt.contact?.mikrowispId 
        ? `ID ${selectedReceipt.contact.mikrowispId}` 
        : selectedReceipt.contact?.name || selectedReceipt.phoneNumber;
      
      const confirmMessage = `⚠️ ADVERTENCIA: RECIBO DUPLICADO\n\n` +
        `Este recibo fue marcado como DUPLICADO\n` +
        `Cliente: ${clientInfo}\n` +
        `Recibo original: ${duplicateId}\n\n` +
        `¿Está seguro de que desea aplicar el pago de todos modos?\n\n` +
        `Esto podría resultar en un doble pago al cliente.`;
      
      if (!window.confirm(confirmMessage)) {
        return;
      }
    }

    try {
      setApplyingPayment(true);
      setMessage({ type: '', text: '' });

      const response = await axios.post(`/api/receipts/${selectedReceipt._id}/approve`, {
        amount: reviewData.amount || selectedReceipt.verificationResult?.detectedAmount,
        bank: reviewData.bank || selectedReceipt.bank,
        type: reviewData.type || selectedReceipt.type,
        date: reviewData.date || selectedReceipt.date,
        time: reviewData.time || selectedReceipt.time,
        notes: reviewData.notes
      });

      if (response.data.success) {
        setMessage({ 
          type: 'success', 
          text: response.data.message || 'Recibo aprobado exitosamente' 
        });
        
        // Recargar lista
        setTimeout(() => {
          loadReceipts();
          setSelectedReceipt(null);
          setReviewData({
            amount: '',
            bank: 'BANRESERVAS',
            type: 'transferencia',
            date: '',
            time: '',
            notes: ''
          });
        }, 2000);
      } else {
        setMessage({ type: 'error', text: response.data.error || 'Error al aprobar recibo' });
      }
    } catch (error) {
      console.error('Error aprobando recibo:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Error al aprobar recibo' 
      });
    } finally {
      setApplyingPayment(false);
    }
  };

  const handleDelete = async (receiptId, e) => {
    e.stopPropagation(); // Evitar que se seleccione el recibo al hacer clic en eliminar
    
    // Encontrar el recibo que se va a eliminar
    const receiptToDelete = receipts.find(r => r._id === receiptId);
    let confirmMessage = 'Ests seguro de que quieres eliminar este recibo? Esta accin no se puede deshacer.';
    
    // Si es duplicado, mostrar información adicional
    if (receiptToDelete?.status === 'duplicate' && receiptToDelete?.verificationResult?.duplicateReceiptId) {
      const duplicateId = receiptToDelete.verificationResult.duplicateReceiptId;
      const clientInfo = receiptToDelete.contact?.mikrowispId 
        ? `ID: ${receiptToDelete.contact.mikrowispId}` 
        : receiptToDelete.phoneNumber;
      confirmMessage = `RECIBO DUPLICADO\n\nCliente: ${clientInfo}\nRecibo original: ${duplicateId}\n\nDesea eliminar este recibo duplicado?`;
    }
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setLoading(true);
      setMessage({ type: '', text: '' });

      const response = await axios.delete(`/api/receipts/${receiptId}`);

      if (response.data.success) {
        setMessage({ 
          type: 'success', 
          text: 'Recibo eliminado exitosamente' 
        });
        
        // Si el recibo eliminado estaba seleccionado, limpiar la selecciâ”œâ”‚n
        if (selectedReceipt && selectedReceipt._id === receiptId) {
          setSelectedReceipt(null);
          setReviewData({
            amount: '',
            bank: 'BANRESERVAS',
            type: 'transferencia',
            date: '',
            time: '',
            notes: ''
          });
        }
        
        // Recargar lista
        setTimeout(() => {
          loadReceipts();
        }, 1000);
      } else {
        setMessage({ type: 'error', text: response.data.error || 'Error al eliminar recibo' });
      }
    } catch (error) {
      console.error('Error eliminando recibo:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Error al eliminar recibo' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedReceipt) return;

    if (!window.confirm('Ests seguro de rechazar este recibo?')) {
      return;
    }

    try {
      setApplyingPayment(true);
      setMessage({ type: '', text: '' });

      const response = await axios.post(`/api/receipts/${selectedReceipt._id}/reject`, {
        reason: reviewData.notes || 'Recibo rechazado por el administrador'
      });

      if (response.data.success) {
        setMessage({ type: 'success', text: 'Recibo rechazado exitosamente' });
        
        setTimeout(() => {
          loadReceipts();
          setSelectedReceipt(null);
        }, 2000);
      }
    } catch (error) {
      console.error('Error rechazando recibo:', error);
      setMessage({ type: 'error', text: 'Error al rechazar recibo' });
    } finally {
      setApplyingPayment(false);
    }
  };

  const handleApplyPayment = async () => {
    if (!selectedReceipt) return;

    // Advertencia para recibos duplicados
    if (selectedReceipt.status === 'duplicate') {
      const duplicateId = selectedReceipt.verificationResult?.duplicateReceiptId || 'desconocido';
      const clientInfo = selectedReceipt.contact?.mikrowispId 
        ? `ID ${selectedReceipt.contact.mikrowispId}` 
        : selectedReceipt.contact?.name || selectedReceipt.phoneNumber;
      
      const confirmMessage = `⚠️ ADVERTENCIA: RECIBO DUPLICADO\n\n` +
        `Este recibo fue marcado como DUPLICADO\n` +
        `Cliente: ${clientInfo}\n` +
        `Recibo original: ${duplicateId}\n\n` +
        `¿Está seguro de que desea aplicar el pago manualmente?\n\n` +
        `Esto podría resultar en un doble pago al cliente.`;
      
      if (!window.confirm(confirmMessage)) {
        return;
      }
    }

    try {
      setApplyingPayment(true);
      setMessage({ type: '', text: '' });

      const response = await axios.post(`/api/receipts/${selectedReceipt._id}/apply-payment`, {
        amount: reviewData.amount || selectedReceipt.verificationResult?.detectedAmount,
        pasarela: reviewData.bank || selectedReceipt.bank || 'BANRESERVAS',
        fecha: reviewData.date || selectedReceipt.date || (() => {
          const now = new Date();
          const rdString = now.toLocaleString('en-US', { timeZone: 'America/Santo_Domingo' });
          const rdDate = new Date(rdString);
          return rdDate.toISOString().slice(0, 19).replace('T', ' ');
        })()
      });

      if (response.data.success) {
        setMessage({ type: 'success', text: 'Pago aplicado exitosamente' });
        
        setTimeout(() => {
          loadReceipts();
          setSelectedReceipt(null);
        }, 2000);
      }
    } catch (error) {
      console.error('Error aplicando pago:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Error al aplicar pago' 
      });
    } finally {
      setApplyingPayment(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { text: 'Pendiente', class: 'status-pending' },
      verified: { text: 'Verificado', class: 'status-verified' },
      fake: { text: 'Falso', class: 'status-fake' },
      duplicate: { text: 'Duplicado', class: 'status-duplicate' },
      approved: { text: 'Aprobado', class: 'status-approved' },
      rejected: { text: 'Rechazado', class: 'status-rejected' }
    };
    const badge = badges[status] || badges.pending;
    return <span className={`status-badge ${badge.class}`}>{badge.text}</span>;
  };

  if (!isOpen) return null;

  return (
    <div className="receipt-review-overlay" onClick={onClose}>
      <div className="receipt-review-panel" onClick={(e) => e.stopPropagation()}>
        <div className="receipt-review-header">
          <h2>🧾 Revisión de Recibos Bancarios</h2>
          <button className="close-button" onClick={onClose}>✖</button>
        </div>

        {message.text && (
          <div className={`receipt-review-message ${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="receipt-review-content">
          <div className="receipt-list">
            <h3>Recibos Pendientes y Aplicados ({receipts.length})</h3>
            {loading ? (
              <div className="loading">Cargando recibos...</div>
            ) : receipts.length === 0 ? (
              <div className="empty-state">No hay recibos pendientes de revisiâ”œâ”‚n</div>
            ) : (
              <div className="receipt-items">
                {receipts.map(receipt => (
                  <div
                    key={receipt._id}
                    className={`receipt-item ${selectedReceipt?._id === receipt._id ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedReceipt(receipt);
                      setReviewData({
                        amount: receipt.verificationResult?.detectedAmount || receipt.amount || '',
                        bank: receipt.bank || receipt.verificationResult?.detectedBank || 'BANRESERVAS',
                        type: receipt.type || receipt.verificationResult?.detectedType || 'transferencia',
                        date: receipt.date ? toDateTimeLocalRD(receipt.date).slice(0, 10) : '',
                        time: receipt.time || receipt.verificationResult?.detectedTime || '',
                        notes: ''
                      });
                      // Resetear zoom al cambiar de recibo
                      setImageZoomed(false);
                      setZoomLevel(1);
                      setImagePosition({ x: 0, y: 0 });
                      // Resetear ID manual
                      setManualMikrowispId('');
                      // Cargar deuda del cliente si tiene mikrowispId
                      setClientDebt(null);
                      if (receipt.contact?.mikrowispId) {
                        loadClientDebt(receipt.contact.mikrowispId);
                      }
                    }}
                  >
                    <div className="receipt-item-header">
                      <span className="receipt-phone">{receipt.phoneNumber}</span>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        {receipt.autoApplied && (
                          <span className="auto-applied-badge" title="Pago aplicado automticamente">
                             Auto
                          </span>
                        )}
                        {getStatusBadge(receipt.status)}
                        <button
                          className="delete-receipt-btn"
                          onClick={(e) => handleDelete(receipt._id, e)}
                          title="Eliminar recibo"
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#dc3545',
                            cursor: 'pointer',
                            padding: '4px 8px',
                            fontSize: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '4px',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = 'rgba(220, 53, 69, 0.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                          }}
                        >
                          
                        </button>
                      </div>
                    </div>
                    {receipt.contact && (
                      <div className="receipt-contact">
                        {receipt.contact.name} {receipt.contact.mikrowispId && `(ID: ${receipt.contact.mikrowispId})`}
                      </div>
                    )}
                    <div className="receipt-info">
                      {receipt.bank && <span className="receipt-bank">{receipt.bank}</span>}
                      {receipt.amount && <span className="receipt-amount">${receipt.amount.toFixed(2)}</span>}
                    </div>
                    {receipt.status === 'duplicate' && receipt.verificationResult?.duplicateReceiptId && (
                      <div className="duplicate-info" style={{ 
                        fontSize: '11px', 
                        color: '#856404',
                        backgroundColor: '#fff3cd',
                        border: '1px solid #ffeaa7',
                        borderRadius: '4px',
                        padding: '5px 8px',
                        marginTop: '5px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '3px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{ fontSize: '14px' }}>⚠️</span>
                          <span>
                            Duplicado enviado por <strong>{receipt.contact?.mikrowispId ? `ID ${receipt.contact.mikrowispId}` : receipt.contact?.name || receipt.phoneNumber}</strong>
                          </span>
                        </div>
                        <div style={{ fontSize: '10px', color: '#666', paddingLeft: '18px' }}>
                          Recibo original: {receipt.verificationResult.duplicateReceiptId}
                        </div>
                      </div>
                    )}
                    {(receipt.verificationResult?.transactionNumber || receipt.verificationResult?.confirmationNumber) && (
                      <div className="receipt-transaction-info" style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                         Transf: {receipt.verificationResult?.transactionNumber || receipt.verificationResult?.confirmationNumber}
                      </div>
                    )}
                    {receipt.appliedPaymentId && (
                      <div className="receipt-payment-info" style={{ fontSize: '12px', color: '#28a745', marginTop: '4px' }}>
                         Pago ID: {receipt.appliedPaymentId}
                      </div>
                    )}
                    <div className="receipt-date">
                      {receipt.date ? formatDateShortRD(receipt.date) : 'Sin fecha'}
                      {receipt.time && ` - ${receipt.time}`}
                    </div>
                    {receipt.createdAt && (
                      <div className="receipt-received-date" style={{ 
                        fontSize: '11px', 
                        color: '#6c757d', 
                        marginTop: '4px' 
                      }}>
                         Recibido: {formatDateShortRD(receipt.createdAt)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="receipt-detail">
            {selectedReceipt ? (
              <>
                <h3>Detalles del Recibo</h3>
                <div className="receipt-image-container">
                  <img 
                    src={selectedReceipt._id 
                      ? `/api/receipts/image/${selectedReceipt._id}`
                      : selectedReceipt.imagePath?.startsWith('/uploads/') 
                        ? selectedReceipt.imagePath 
                        : selectedReceipt.imagePath?.startsWith('/')
                          ? selectedReceipt.imagePath
                          : `/uploads${selectedReceipt.imagePath?.startsWith('/') ? '' : '/'}${selectedReceipt.imagePath}`}
                    alt="Recibo"
                    className="receipt-image"
                    style={{ cursor: 'zoom-in' }}
                    onClick={() => {
                      setImageZoomed(true);
                      setZoomLevel(1);
                      setImagePosition({ x: 0, y: 0 });
                    }}
                    onError={(e) => {
                      console.error('Error cargando imagen del recibo:', selectedReceipt.imagePath);
                      // Intentar con ruta directa si falla la API
                      if (e.target.src.includes('/api/receipts/image/')) {
                        const directPath = selectedReceipt.imagePath?.startsWith('/uploads/') 
                          ? selectedReceipt.imagePath 
                          : `/uploads${selectedReceipt.imagePath?.startsWith('/') ? '' : '/'}${selectedReceipt.imagePath}`;
                        e.target.src = directPath;
                      } else {
                        // Intentar con ruta alternativa
                        const altPath = selectedReceipt.imagePath?.replace(/^\/uploads\//, '/uploads/media/');
                        if (altPath !== e.target.src) {
                          e.target.src = altPath;
                        } else {
                          e.target.style.display = 'none';
                          const errorDiv = document.createElement('div');
                          errorDiv.className = 'receipt-image-error';
                          errorDiv.textContent = ' Error al cargar imagen';
                          errorDiv.style.cssText = 'padding: 20px; text-align: center; color: #dc3545;';
                          e.target.parentNode.appendChild(errorDiv);
                        }
                      }
                    }}
                  />
                  <div style={{ marginTop: '8px', fontSize: '12px', color: '#666', textAlign: 'center' }}>
                     Haz clic para ampliar   Usa la rueda del mouse para hacer zoom
                  </div>
                </div>

                {/* Modal de imagen ampliada con zoom */}
                {imageZoomed && (
                  <div 
                    className="image-zoom-modal"
                    style={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.95)',
                      zIndex: 20000,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: zoomLevel > 1 ? 'move' : 'zoom-out'
                    }}
                    onClick={(e) => {
                      if (e.target === e.currentTarget) {
                        setImageZoomed(false);
                        setZoomLevel(1);
                        setImagePosition({ x: 0, y: 0 });
                      }
                    }}
                    onWheel={(e) => {
                      e.preventDefault();
                      const delta = e.deltaY > 0 ? -0.1 : 0.1;
                      setZoomLevel(prev => {
                        const newZoom = Math.max(0.5, Math.min(5, prev + delta));
                        return newZoom;
                      });
                    }}
                    onMouseDown={(e) => {
                      if (zoomLevel > 1 && e.target.tagName === 'IMG') {
                        setIsDragging(true);
                        setDragStart({
                          x: e.clientX - imagePosition.x,
                          y: e.clientY - imagePosition.y
                        });
                      }
                    }}
                    onMouseMove={(e) => {
                      if (isDragging && zoomLevel > 1) {
                        setImagePosition({
                          x: e.clientX - dragStart.x,
                          y: e.clientY - dragStart.y
                        });
                      }
                    }}
                    onMouseUp={() => {
                      setIsDragging(false);
                    }}
                    onMouseLeave={() => {
                      setIsDragging(false);
                    }}
                  >
                    <button
                      onClick={() => {
                        setImageZoomed(false);
                        setZoomLevel(1);
                        setImagePosition({ x: 0, y: 0 });
                      }}
                      style={{
                        position: 'absolute',
                        top: '20px',
                        right: '20px',
                        background: 'rgba(255, 255, 255, 0.2)',
                        border: 'none',
                        color: 'white',
                        fontSize: '32px',
                        width: '50px',
                        height: '50px',
                        borderRadius: '50%',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 20001,
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                      }}
                    >
                      
                    </button>
                    <div
                      style={{
                        position: 'absolute',
                        top: '20px',
                        left: '20px',
                        color: 'white',
                        fontSize: '14px',
                        zIndex: 20001,
                        background: 'rgba(0, 0, 0, 0.5)',
                        padding: '8px 12px',
                        borderRadius: '6px'
                      }}
                    >
                      Zoom: {Math.round(zoomLevel * 100)}%  Doble clic para resetear
                    </div>
                    <img
                      src={selectedReceipt._id 
                        ? `/api/receipts/image/${selectedReceipt._id}`
                        : selectedReceipt.imagePath?.startsWith('/uploads/') 
                          ? selectedReceipt.imagePath 
                          : selectedReceipt.imagePath?.startsWith('/')
                            ? selectedReceipt.imagePath
                            : `/uploads${selectedReceipt.imagePath?.startsWith('/') ? '' : '/'}${selectedReceipt.imagePath}`}
                      alt="Recibo ampliado"
                      style={{
                        maxWidth: '90vw',
                        maxHeight: '90vh',
                        transform: `scale(${zoomLevel}) translate(${imagePosition.x / zoomLevel}px, ${imagePosition.y / zoomLevel}px)`,
                        transition: zoomLevel === 1 ? 'transform 0.2s' : 'none',
                        cursor: zoomLevel > 1 ? 'move' : 'zoom-out',
                        userSelect: 'none'
                      }}
                      onDoubleClick={() => {
                        setZoomLevel(1);
                        setImagePosition({ x: 0, y: 0 });
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (zoomLevel === 1) {
                          setImageZoomed(false);
                        }
                      }}
                      draggable={false}
                    />
                  </div>
                )}

                {selectedReceipt.autoApplied && (
                  <div className="auto-applied-notice" style={{
                    padding: '12px 16px',
                    backgroundColor: '#d4edda',
                    border: '1px solid #c3e6cb',
                    borderRadius: '6px',
                    marginBottom: '20px',
                    color: '#155724'
                  }}>
                    <strong> Pago Aplicado Automticamente</strong>
                    {selectedReceipt.appliedPaymentId && (
                      <div style={{ marginTop: '4px', fontSize: '13px' }}>
                        ID de Factura: {selectedReceipt.appliedPaymentId}
                      </div>
                    )}
                    <div style={{ marginTop: '4px', fontSize: '13px' }}>
                      Fecha de aplicaciâ”œâ”‚n: {formatDateTimeRD(selectedReceipt.updatedAt, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                    {selectedReceipt.createdAt && (
                      <div style={{ marginTop: '4px', fontSize: '13px', color: '#6c757d' }}>
                        Recibido: {formatDateTimeRD(selectedReceipt.createdAt, {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Mostrar deuda del cliente si tiene mikrowispId */}
                {selectedReceipt.contact?.mikrowispId && (
                  <div style={{
                    padding: '14px 18px',
                    backgroundColor: '#fff5f5',
                    border: '2px solid #dc3545',
                    borderRadius: '8px',
                    marginBottom: '20px'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      marginBottom: '8px'
                    }}>
                      <strong style={{ fontSize: '16px', color: '#333' }}>
                        Informacion del Cliente
                      </strong>
                      {loadingDebt && (
                        <span style={{ fontSize: '12px', color: '#666' }}>Cargando...</span>
                      )}
                    </div>
                    
                    <div style={{ fontSize: '14px', color: '#495057', marginBottom: '4px' }}>
                      <strong>Cliente:</strong> {selectedReceipt.contact?.name || 'Sin nombre'}
                    </div>
                    
                    <div style={{ fontSize: '14px', color: '#495057', marginBottom: '4px' }}>
                      <strong> Telefono:</strong> {selectedReceipt.phoneNumber || selectedReceipt.contact?.phoneNumber || 'Sin telefono'}
                    </div>
                    
                    <div style={{ fontSize: '14px', color: '#495057', marginBottom: '8px' }}>
                      <strong> ID Mikrowisp:</strong> {selectedReceipt.contact.mikrowispId}
                    </div>
                    
                    {clientDebt && (
                      <>
                        <div style={{ 
                          fontSize: '20px', 
                          fontWeight: 'bold', 
                          color: '#dc3545',
                          marginTop: '10px',
                          padding: '10px',
                          backgroundColor: '#ffffff',
                          borderRadius: '6px',
                          border: '1px solid #dc3545'
                        }}>
                           DEUDA TOTAL: RD$ {clientDebt.amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        </div>
                        
                        {clientDebt.unpaidInvoices > 0 && (
                          <div style={{ 
                            marginTop: '8px', 
                            fontSize: '13px', 
                            color: '#666',
                            fontStyle: 'italic'
                          }}>
                             Facturas pendientes: {clientDebt.unpaidInvoices}
                          </div>
                        )}
                      </>
                    )}
                    
                    {!loadingDebt && !clientDebt && (
                      <div style={{ 
                        marginTop: '8px', 
                        fontSize: '13px', 
                        color: '#666',
                        fontStyle: 'italic'
                      }}>
                         No se pudo cargar informacin de deuda
                      </div>
                    )}
                  </div>
                )}

                {/* Mostrar campo para ingresar ID cuando el contacto NO tiene mikrowispId */}
                {!selectedReceipt.contact?.mikrowispId && (
                  <div style={{
                    padding: '14px 18px',
                    backgroundColor: '#fff9e6',
                    border: '2px solid #ffc107',
                    borderRadius: '8px',
                    marginBottom: '20px'
                  }}>
                    <div style={{ 
                      marginBottom: '12px'
                    }}>
                      <strong style={{ fontSize: '16px', color: '#856404' }}>
                        âš ï¸ Cliente sin ID de Mikrowisp Registrado
                      </strong>
                    </div>
                    
                    <div style={{ fontSize: '14px', color: '#495057', marginBottom: '4px' }}>
                      <strong>Cliente:</strong> {selectedReceipt.contact?.name || 'Sin nombre'}
                    </div>
                    
                    <div style={{ fontSize: '14px', color: '#495057', marginBottom: '12px' }}>
                      <strong> Telefono:</strong> {selectedReceipt.phoneNumber || selectedReceipt.contact?.phoneNumber || 'Sin telefono'}
                    </div>
                    
                    <div style={{ 
                      padding: '12px',
                      backgroundColor: '#ffffff',
                      borderRadius: '6px',
                      border: '1px solid #ffc107'
                    }}>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '8px',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        color: '#856404'
                      }}>
                         Ingresa el ID de Mikrowisp del cliente:
                      </label>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input
                          type="number"
                          value={manualMikrowispId}
                          onChange={(e) => setManualMikrowispId(e.target.value)}
                          placeholder="Ej: 12345"
                          style={{
                            flex: 1,
                            padding: '10px 12px',
                            border: '2px solid #ffc107',
                            borderRadius: '6px',
                            fontSize: '14px',
                            outline: 'none'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#ff9800';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#ffc107';
                          }}
                        />
                        <button
                          onClick={handleSaveMikrowispId}
                          disabled={!manualMikrowispId || savingMikrowispId}
                          style={{
                            padding: '10px 20px',
                            backgroundColor: manualMikrowispId && !savingMikrowispId ? '#28a745' : '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            cursor: manualMikrowispId && !savingMikrowispId ? 'pointer' : 'not-allowed',
                            transition: 'background-color 0.2s',
                            whiteSpace: 'nowrap'
                          }}
                          onMouseEnter={(e) => {
                            if (manualMikrowispId && !savingMikrowispId) {
                              e.target.style.backgroundColor = '#218838';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (manualMikrowispId && !savingMikrowispId) {
                              e.target.style.backgroundColor = '#28a745';
                            }
                          }}
                        >
                          {savingMikrowispId ? ' Guardando...' : ' Guardar ID'}
                        </button>
                      </div>
                      <small style={{ 
                        display: 'block',
                        marginTop: '8px',
                        fontSize: '12px',
                        color: '#666',
                        fontStyle: 'italic'
                      }}>
                         Este ID se asociara al contacto para poder aplicar el pago correctamente
                      </small>
                    </div>
                  </div>
                )}

                {(selectedReceipt.date || selectedReceipt.time) && (
                  <div className="receipt-date-time-info" style={{
                    padding: '10px 14px',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    marginBottom: '20px',
                    fontSize: '14px'
                  }}>
                    <strong> Fecha y Hora del Recibo:</strong>
                    <div style={{ marginTop: '6px', color: '#495057' }}>
                      {selectedReceipt.date 
                        ? formatDateRD(selectedReceipt.date, {
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })
                        : 'Sin fecha'}
                      {selectedReceipt.time && ` - ${selectedReceipt.time}`}
                    </div>
                  </div>
                )}

                <div className="receipt-details-form">
                  <div className="form-group">
                    <label>Monto</label>
                    <input
                      type="number"
                      step="0.01"
                      value={reviewData.amount}
                      onChange={(e) => setReviewData({ ...reviewData, amount: e.target.value })}
                      placeholder="Monto del recibo"
                    />
                  </div>

                  <div className="form-group">
                    <label>Banco</label>
                    <select
                      value={reviewData.bank}
                      onChange={(e) => setReviewData({ ...reviewData, bank: e.target.value })}
                    >
                      <option value="BANRESERVAS">BANRESERVAS</option>
                      <option value="BANCO POPULAR">BANCO POPULAR</option>
                      <option value="BANCO BHD LEON">BANCO BHD LEON</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Tipo</label>
                    <select
                      value={reviewData.type}
                      onChange={(e) => setReviewData({ ...reviewData, type: e.target.value })}
                    >
                      <option value="transferencia">Transferencia</option>
                      <option value="deposito">Depâ”œâ”‚sito</option>
                      <option value="sucursal">Sucursal/Agente</option>
                      <option value="cajero">Pago por Cajero</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Fecha</label>
                    <input
                      type="date"
                      value={reviewData.date}
                      onChange={(e) => setReviewData({ ...reviewData, date: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label>Hora</label>
                    <input
                      type="time"
                      value={reviewData.time}
                      onChange={(e) => setReviewData({ ...reviewData, time: e.target.value })}
                      placeholder="HH:MM"
                    />
                    {selectedReceipt.time && !reviewData.time && (
                      <small className="form-help" style={{ color: '#666', fontStyle: 'italic' }}>
                        Hora detectada: {selectedReceipt.time}
                      </small>
                    )}
                  </div>

                  {(selectedReceipt.verificationResult?.transactionNumber || selectedReceipt.verificationResult?.confirmationNumber) && (
                    <div className="form-group">
                      <label>Nâ”œâ•‘mero de Transferencia/Confirmaciâ”œâ”‚n</label>
                      <input
                        type="text"
                        value={selectedReceipt.verificationResult?.transactionNumber || selectedReceipt.verificationResult?.confirmationNumber || ''}
                        readOnly
                        style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        title="Nâ”œâ•‘mero de transferencia (solo para referencia en la base de datos)"
                      />
                    </div>
                  )}

                  <div className="form-group">
                    <label>Notas</label>
                    <textarea
                      value={reviewData.notes}
                      onChange={(e) => setReviewData({ ...reviewData, notes: e.target.value })}
                      placeholder="Notas adicionales..."
                      rows={3}
                    />
                  </div>

                  {selectedReceipt.verificationResult?.errors && selectedReceipt.verificationResult.errors.length > 0 && (
                    <div className="verification-errors">
                      <strong>Errores detectados:</strong>
                      <ul>
                        {selectedReceipt.verificationResult.errors.map((error, idx) => (
                          <li key={idx}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="receipt-actions">
                    {(selectedReceipt.status === 'pending' || selectedReceipt.status === 'duplicate') && !selectedReceipt.autoApplied && (
                      <>
                        {selectedReceipt.status === 'duplicate' && (
                          <div style={{
                            padding: '10px 14px',
                            backgroundColor: '#fff3cd',
                            border: '2px solid #ffc107',
                            borderRadius: '6px',
                            marginBottom: '12px',
                            color: '#856404',
                            fontSize: '13px',
                            fontWeight: '500'
                          }}>
                            ⚠️ <strong>ADVERTENCIA:</strong> Este recibo está marcado como DUPLICADO. 
                            Aplicar el pago podría resultar en un doble pago al cliente.
                          </div>
                        )}
                        <button
                          className="btn-approve"
                          onClick={handleApprove}
                          disabled={applyingPayment}
                          style={selectedReceipt.status === 'duplicate' ? {
                            backgroundColor: '#ffc107',
                            borderColor: '#ffc107'
                          } : {}}
                        >
                          {applyingPayment ? 'Procesando...' : (selectedReceipt.status === 'duplicate' ? '⚠️ Aprobar Duplicado' : ' Aprobar y Aplicar Pago')}
                        </button>
                        <button
                          className="btn-apply"
                          onClick={handleApplyPayment}
                          disabled={applyingPayment}
                          style={selectedReceipt.status === 'duplicate' ? {
                            backgroundColor: '#ffc107',
                            borderColor: '#ffc107'
                          } : {}}
                        >
                          {applyingPayment ? 'Aplicando...' : (selectedReceipt.status === 'duplicate' ? '⚠️ Aplicar Duplicado' : ' Aplicar Pago Manualmente')}
                        </button>
                        <button
                          className="btn-reject"
                          onClick={handleReject}
                          disabled={applyingPayment}
                        >
                          {applyingPayment ? 'Rechazando...' : ' Rechazar'}
                        </button>
                      </>
                    )}
                    {selectedReceipt.autoApplied && (
                      <div style={{
                        padding: '12px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                        textAlign: 'center',
                        color: '#666',
                        fontSize: '14px'
                      }}>
                        Este recibo fue procesado y aplicado automticamente por el sistema.
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="no-selection">
                <p>Selecciona un recibo de la lista para revisarlo</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptReviewPanel;

